import { apiClient } from "@/api";

/**
 * The three calls behind the **Remember me** control.
 *
 * Deliberately thin. The interesting decisions are all on the server —
 * what an identity is, what it stores, what deleting it deletes — and a
 * client that reproduced any of them would be a second source of truth
 * about a promise the page makes to a person.
 *
 * ## Why there is no visitor id here
 *
 * The server answers one bit. It could send the id, and the page has no
 * use for it: the label, the pill and the button all key on a boolean.
 * An identifier the visitor never asked to have would then be sitting in
 * a React tree, in a screen recording, and in anything that serialises
 * component state, in exchange for nothing.
 *
 * ## Why there is no cookie code here
 *
 * The cookie is `HttpOnly`, so `document.cookie` cannot see it and
 * nothing on this page can lift it. That is the point rather than an
 * inconvenience: a script injected into the page cannot steal a
 * returning visitor's identity if the browser will not hand it over.
 * The consequence is that "am I remembered" is a question only the
 * server can answer, which is why {@link readRemembered} exists at all
 * instead of a `document.cookie` check.
 */

/** What every `/v1/rehearsal/remember` call returns. */
export interface RememberedState {
  readonly remembered: boolean;
  /**
   * How long being remembered lasts without a return visit.
   *
   * Served rather than written into the copy module, because the
   * control says this number out loud to the visitor. A page promising
   * thirty days against a store enforcing seven would be wrong in the
   * one place a reader has no way to check.
   */
  readonly expires_in_days: number;
}

/**
 * The state a fresh page assumes before the server has answered.
 *
 * Not remembered, and a duration that is never rendered because the
 * note carrying it only appears once a real answer has arrived. Guessing
 * "remembered" would flash a **Forget me** button at a visitor who has
 * no record to delete.
 */
export const UNKNOWN_REMEMBERED: RememberedState = {
  remembered: false,
  expires_in_days: 0,
};

/** Is this browser remembered? Never throws for "no" — that is a 200. */
export function readRemembered(): Promise<RememberedState> {
  return apiClient.get<RememberedState>("/v1/rehearsal/remember");
}

/** Start remembering. Idempotent server-side, so a double press is safe. */
export function startRemembering(): Promise<RememberedState> {
  return apiClient.post<RememberedState>("/v1/rehearsal/remember", { body: {} });
}

/**
 * Stop, and delete what was remembered.
 *
 * The caller must set its state from what this resolves to rather than
 * assuming success, so a failed delete leaves the pill saying she still
 * remembers — which is the truth, and the direction to be wrong in.
 */
export function stopRemembering(): Promise<RememberedState> {
  return apiClient.delete<RememberedState>("/v1/rehearsal/remember");
}
